import { z } from 'zod';

/**
 * Schema INPUT kind. Drives which keypad layout the referee gets and
 * how shots map onto raw points.
 *
 *   * ring_value   — sum of per-shot point values (precision /
 *                    reduced layouts; numeric keypad). Innermost zone
 *                    counts as "inner ten" for tie-break.
 *   * hit_factor   — IPSC-style A/C/D/M/NS zone keypad. Zone-to-points
 *                    lookup driven by major/minor power knobs.
 *
 * The precision-vs-speed decision (sum vs sum÷time) is orthogonal —
 * see ScoringMode below. Pre-0031 there was a third kind
 * `speed_ring_value`; it was collapsed into `ring_value` since it
 * shared the same input layout.
 */
export const ScoringSchemaKind = z.enum([
  'ring_value',
  'hit_factor',
]);
export type ScoringSchemaKind = z.infer<typeof ScoringSchemaKind>;

/**
 * Output mode. Picked by the referee per drill instantiation —
 * any drill can be shot in either mode.
 *
 *   * precision — sum of points; time recorded but optional. Ranking
 *                 by points → inner-tens → fastest time.
 *   * speed     — points ÷ total time (hit factor). Time required.
 *                 Ranking by hitFactor → rawScore.
 */
export const ScoringMode = z.enum(['precision', 'speed']);
export type ScoringMode = z.infer<typeof ScoringMode>;

/**
 * The 4 originally-seeded codes survive as informational identifiers
 * (back-compat: stored on stages, surfaced in PDFs / audit trails),
 * but they no longer drive behaviour — `kind` does. Admins can create
 * schemas with any free-form code.
 */
export const ScoringSchemaCode = z.string().min(1);
export type ScoringSchemaCode = z.infer<typeof ScoringSchemaCode>;

export const RingValueConfig = z.object({
  kind: z.literal('ring_value'),
  /** Optional informational code (e.g. 'precision_10_0'). */
  code: z.string().optional(),
  /**
   * Allowed per-shot values. Derived at the API boundary from the
   * schema's `scoring_schema_zones` rows (deduped numeric values),
   * so admin-created schemas with arbitrary zone lists work without
   * code changes.
   */
  allowed: z.array(z.number().int()).default([10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0]),
  /**
   * Value of the inner-most zone (counted as "inner ten" for
   * tie-break ranking). Defaults to max(allowed).
   */
  innerTenValue: z.number().int().optional(),
});
export type RingValueConfig = z.infer<typeof RingValueConfig>;

export const IpscZone = z.enum(['A', 'C', 'D', 'M', 'NS']);
export type IpscZone = z.infer<typeof IpscZone>;

export const HitFactorConfig = z.object({
  kind: z.literal('hit_factor'),
  code: z.string().optional(),
  power: z.enum(['major', 'minor']).default('minor'),
  alphaPoints: z.number().int().default(5),
  charlieMajor: z.number().int().default(4),
  charlieMinor: z.number().int().default(3),
  deltaMajor: z.number().int().default(2),
  deltaMinor: z.number().int().default(1),
  missPenalty: z.number().int().default(-10),
  noShootPenalty: z.number().int().default(-10),
  proceduralPenalty: z.number().int().default(-10),
  targetCount: z.number().int().nonnegative().default(0),
});
export type HitFactorConfig = z.infer<typeof HitFactorConfig>;

export const ScoringConfig = z.discriminatedUnion('kind', [
  RingValueConfig,
  HitFactorConfig,
]);
export type ScoringConfig = z.infer<typeof ScoringConfig>;

export interface Shot {
  ord: number;
  valueNumeric: number | null;
  zoneCode: IpscZone | null;
  firstShotTimeMs: number | null;
  elapsedTimeMs: number | null;
}

export interface Penalty {
  code: 'procedural' | 'miss' | 'no_shoot' | 'extra_shot';
  points: number;
}

export interface Stage {
  shotCount: number;
  timeLimitS: number | null;
  scoring: ScoringConfig;
  /**
   * Output semantics chosen at instantiation time. Drives whether
   * `scoreRound` divides by time and which ranking applies.
   */
  mode: ScoringMode;
}

export interface RoundResult {
  rawScore: number;
  shotCount: number;
  innerTens: number;
  hitFactor: number | null;
  totalTimeMs: number | null;
  firstShotTimeMs: number | null;
  invalid: boolean;
  reasons: string[];
}
